// =============================================================================
// ImportPasteCard: the "paste in your resume / LinkedIn text" flow the brief
// asks for. Posts to the SAME POST /corpus/import route apps/web's
// PasteImportSheet uses (importCorpusText in lib/api.ts, the wire-identical
// copy of useCorpus.ts's importPaste). Progress + result are surfaced
// HONESTLY per the brief's law (d): there is no server-side progress stream
// for this route (it's a single synchronous round trip — see api.ts's
// ImportResult doc comment), so this shows an indeterminate "reading…" state
// while the request is in flight and the REAL counts the server returned once
// it resolves — never an invented percentage.
//
// FILE-DOOR (2026-07-05) adds a SECOND door onto the exact same pipeline: "or
// upload a PDF / Word file" below the paste textarea. A file goes to POST
// /corpus/import-file (importCorpusFile in lib/api.ts), which stores the
// original blob in R2, extracts its text server-side, and — if extraction
// clears the honest substantive floor — runs through the SAME server-side
// import-core (runCorpusImport) the paste path calls, so the merge-report
// rendered below (describeResult) is IDENTICAL regardless of which door the
// text came through. A file with no readable text layer (a scanned/image-only
// PDF) surfaces the server's honest 422 no_text_layer error with the paste
// fallback suggested right there — never a fabricated parse.
// =============================================================================

import { useRef, useState } from 'react';
import {
  FileImportNoTextError,
  archiveImport,
  importCorpusFile,
  importCorpusText,
} from '../lib/api';
import type { ImportResult } from '../lib/api';
import { Card } from './Card';
import { VoiceTextarea } from './VoiceTextarea';
import styles from './ImportPasteCard.module.css';

type ImportPhase = 'idle' | 'reading' | 'done' | 'error';

/** The file-upload door's own phase, independent of the paste textarea's
 *  phase above (a user can be mid-paste and mid-upload only in sequence, but
 *  keeping them as separate state avoids the two doors fighting over one
 *  shared "reading" label — the affordance line and the textarea disable
 *  independently per which door is actually in flight). 'no-text' is its own
 *  phase (not folded into 'error') so the honest recovery copy — "paste the
 *  text instead" — can point at the SAME textarea sitting right above it. */
type FilePhase = 'idle' | 'uploading' | 'done' | 'error' | 'no-text';

export interface ImportPasteCardProps {
  /** Fires after a successful import so the caller can refresh the canvas. */
  onImported: (result: ImportResult) => void;
  /** E7-WALK-WAVE W4 (2026-07-17) — optional reassurance copy rendered above
   *  the paste textarea (e.g. "paste as much or as little as you like — you
   *  can add more after"). Undefined -> no extra line, so the Refine tab's
   *  existing call site renders byte-identically; this is additive/opt-in,
   *  never a change to this component's default behavior. */
  helperCopy?: string;
  /** E7-WALK-WAVE W4 — opts the paste textarea into a visibly larger target
   *  (min-height, bigger again under the app's own mobile breakpoint —
   *  see ImportPasteCard.module.css). Default false preserves Refine's
   *  existing sizing exactly; the E7 finale is the first caller to opt in
   *  (pasting a long LinkedIn profile one-handed on a phone was "nearly
   *  impossible" — his words). */
  largeTarget?: boolean;
}

const MIN_LENGTH = 20;
const ACCEPTED_FILE_TYPES = '.pdf,.docx';

export function ImportPasteCard({ onImported, helperCopy, largeTarget }: ImportPasteCardProps) {
  const [text, setText] = useState('');
  const [phase, setPhase] = useState<ImportPhase>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<ImportResult | null>(null);

  const [filePhase, setFilePhase] = useState<FilePhase>('idle');
  const [fileErrorMessage, setFileErrorMessage] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // W3 remove-an-import: "undo this import" (the provenance path — the fresh
  // ImportResult carries the corpus_imports id). Archives the whole import + the
  // rows it seeded; reversible from the archived section below the canvas.
  const [undoPhase, setUndoPhase] = useState<'idle' | 'undoing' | 'undone' | 'error'>('idle');
  const [undoError, setUndoError] = useState<string | null>(null);

  const tooShort = text.trim().length > 0 && text.trim().length < MIN_LENGTH;
  const canSubmit = text.trim().length >= MIN_LENGTH && phase !== 'reading';
  const fileBusy = filePhase === 'uploading';

  async function handleSubmit() {
    if (!canSubmit) return;
    setPhase('reading');
    setErrorMessage(null);
    setUndoPhase('idle');
    setUndoError(null);
    try {
      const result = await importCorpusText(text.trim(), 'paste');
      setLastResult(result);
      setPhase('done');
      setText('');
      onImported(result);
    } catch (err) {
      setPhase('error');
      setErrorMessage(err instanceof Error ? err.message : 'Import failed — try again.');
    }
  }

  function handlePickFile() {
    fileInputRef.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset the input immediately so picking the SAME file twice in a row
    // (e.g. retry after a no-text-layer error) still fires a change event.
    e.target.value = '';
    if (!file) return;

    setFileName(file.name);
    setFilePhase('uploading');
    setFileErrorMessage(null);
    setUndoPhase('idle');
    setUndoError(null);

    try {
      const result = await importCorpusFile(file);
      setLastResult(result);
      setFilePhase('done');
      onImported(result);
    } catch (err) {
      if (err instanceof FileImportNoTextError) {
        setFilePhase('no-text');
        setFileErrorMessage(err.hint);
      } else {
        setFilePhase('error');
        setFileErrorMessage(err instanceof Error ? err.message : 'Upload failed — try again.');
      }
    }
  }

  /** W3: undo the import just completed — archive its event + every row it
   *  seeded (the provenance path). Only offered when the fresh result carries an
   *  importId. On success the parent re-fetches (onImported doubles as the
   *  "corpus changed, refresh" signal) so the just-added rows vanish from the
   *  canvas; they stay recoverable in the archived section below. */
  async function handleUndoImport() {
    const importId = lastResult?.importId;
    if (!importId || undoPhase === 'undoing') return;
    setUndoPhase('undoing');
    setUndoError(null);
    try {
      await archiveImport(importId);
      setUndoPhase('undone');
      if (lastResult) onImported(lastResult);
    } catch (err) {
      setUndoPhase('error');
      setUndoError(err instanceof Error ? err.message : 'Could not undo — try again.');
    }
  }

  /** The "review / undo this import" affordance shared by both doors' result
   *  rows. Only shows when the fresh import carried an importId (paste + file
   *  going forward; a cover-letter import has none). */
  const importUndo =
    lastResult?.importId && undoPhase !== 'undone' ? (
      <button
        type="button"
        className={styles.undoImportLink}
        onClick={handleUndoImport}
        disabled={undoPhase === 'undoing'}
      >
        {undoPhase === 'undoing' ? 'Undoing…' : 'Undo this import'}
      </button>
    ) : undoPhase === 'undone' ? (
      <span className={styles.undoneNote}>Removed — recover it in “Archived” below.</span>
    ) : null;

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <div className={styles.title}>Paste your resume or LinkedIn text</div>
        <div className={styles.subtitle}>
          I'll read it and pull out your roles and stories as cards below.
        </div>
      </div>

      {helperCopy ? <p className={styles.helperCopy}>{helperCopy}</p> : null}

      <VoiceTextarea
        label="Paste here"
        value={text}
        onChange={setText}
        placeholder="Paste your resume, CV, or LinkedIn profile text…"
        rows={7}
        disabled={phase === 'reading'}
        hint={tooShort ? `${MIN_LENGTH - text.trim().length} more characters` : undefined}
        textareaClassName={largeTarget ? styles.pasteTextareaLarge : undefined}
      />

      <button
        type="button"
        className={styles.submitButton}
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        {phase === 'reading' ? (
          <>
            <span className={styles.spinner} aria-hidden="true" />
            Reading every word…
          </>
        ) : (
          'Import'
        )}
      </button>

      {phase === 'done' && lastResult ? (
        <div className={styles.resultRow} role="status">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M5 13l4 4L19 7"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{describeResult(lastResult)}</span>
          {importUndo}
        </div>
      ) : null}

      {phase === 'done' && undoError ? (
        <div className={styles.errorRow} role="alert">
          {undoError}
        </div>
      ) : null}

      {phase === 'error' && errorMessage ? (
        <div className={styles.errorRow} role="alert">
          {errorMessage}
        </div>
      ) : null}

      <div className={styles.fileUploadRow}>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          className={styles.fileInputHidden}
          onChange={handleFileChange}
          aria-label="Upload a PDF or Word resume file"
        />
        <button
          type="button"
          className={styles.fileUploadLink}
          onClick={handlePickFile}
          disabled={fileBusy}
        >
          {filePhase === 'uploading' ? (
            <>
              <span className={styles.spinner} aria-hidden="true" />
              Reading {fileName}…
            </>
          ) : (
            'or upload a PDF / Word file'
          )}
        </button>

        {filePhase === 'done' && lastResult ? (
          <div className={styles.resultRow} role="status">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M5 13l4 4L19 7"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{describeResult(lastResult)}</span>
            {importUndo}
          </div>
        ) : null}

        {filePhase === 'done' && undoError ? (
          <div className={styles.errorRow} role="alert">
            {undoError}
          </div>
        ) : null}

        {filePhase === 'no-text' && fileErrorMessage ? (
          <div className={styles.errorRow} role="alert">
            {fileErrorMessage} You can paste the text in the box above instead.
          </div>
        ) : null}

        {filePhase === 'error' && fileErrorMessage ? (
          <div className={styles.errorRow} role="alert">
            {fileErrorMessage}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/** Honest, count-only summary of what the server actually reported — never a
 *  guessed or rounded figure, and never claims a count the response omitted.
 *  Exported (test coverage) — shared verbatim by both the paste door and the
 *  file-upload door's result rows, so the merge-report text is provably
 *  identical between them regardless of which door produced the ImportResult. */
export function describeResult(result: ImportResult): string {
  const parts: string[] = [];
  if (typeof result.rolesCreated === 'number' && result.rolesCreated > 0) {
    parts.push(`${result.rolesCreated} role${result.rolesCreated === 1 ? '' : 's'}`);
  }
  if (typeof result.achievementsCreated === 'number' && result.achievementsCreated > 0) {
    parts.push(
      `${result.achievementsCreated} ${result.achievementsCreated === 1 ? 'story' : 'stories'}`,
    );
  }
  if (parts.length === 0) {
    return 'Read it — nothing new to add (looks like it matched what you already have).';
  }
  return `Found ${parts.join(' and ')}.`;
}
